import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@ais-ui/utils';
import { useEffect, useRef } from 'react';

function easeInOutQuart(x: number): number {
  return x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2;
}
export const smoothScrollBy = (
  element: HTMLElement,
  distance: number,
  duration: number,
) => {
  // Capture scrollTop BEFORE layout flush
  const start = element.scrollTop;

  // Ensure DOM has applied layout changes
  requestAnimationFrame(() => {
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const eased = easeInOutQuart(progress);

      element.scrollTop = start + distance * eased;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  });
};

export const useResizeObserver = (
  ref: React.MutableRefObject<HTMLElement | null>,
  childrenCount: number,
  children: React.ReactNode,
  isActive: boolean,
) => {
  const prevChildrenCountRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const actualChildPosRef = useRef(0);
  const clickedRef = useRef(false);

  // Handle clicks to track last clicked child
  useEffect(() => {
    const handleClick = (ev: MouseEvent) => {
      const element = ref.current;
      if (!element) return;

      const target = ev.target as HTMLElement;
      const clickedChild = Array.from(element.children).find((child) =>
        child.contains(target),
      );
      if (!clickedChild) return;

      const actualChild = Array.from(clickedChild.children).find((child) =>
        child.contains(target),
      ) as HTMLElement | undefined;

      if (actualChild) {
        actualChildPosRef.current = actualChild.getBoundingClientRect().top;
        clickedRef.current = true;
      }
    };

    const element = ref.current;
    if (!element) return;

    element.addEventListener('click', handleClick);

    // Initialize previous values
    prevChildrenCountRef.current = childrenCount;
    prevScrollHeightRef.current = element.scrollHeight;

    return () => element.removeEventListener('click', handleClick);
  }, [childrenCount, ref]);

  // Observe mutations
  useEffect(() => {
    if (!isActive || !ref.current) return;

    const observer = new MutationObserver(() => {
      setTimeout(() => {
        const element = ref.current;
        if (!element || !clickedRef.current) return;

        const scrollHeight = element.scrollHeight;
        const prevScrollHeight = prevScrollHeightRef.current;
        const prevChildrenCount = prevChildrenCountRef.current;

        // Skip if nothing changed
        if (scrollHeight === prevScrollHeight) return;

        // If children increased, just update the refs
        if (childrenCount > prevChildrenCount) {
          prevChildrenCountRef.current = childrenCount;
          prevScrollHeightRef.current = scrollHeight;
          return;
        }

        const childrenHeight = prevScrollHeight / prevChildrenCount;

        const { top: elementY, height: elementHeight } =
          element.getBoundingClientRect();
        const heightDiff = scrollHeight - prevScrollHeight;

        // Only scroll if mouse is near the bottom
        if (
          elementHeight + elementY <
          actualChildPosRef.current + childrenHeight + heightDiff
        ) {
          const scrollAmount =
            actualChildPosRef.current +
            childrenHeight +
            heightDiff -
            elementHeight -
            elementY / 2;
          smoothScrollBy(element, scrollAmount, 500);
        }

        // Update refs
        prevScrollHeightRef.current = scrollHeight;
        clickedRef.current = false;
      }, 150);
    });

    observer.observe(ref.current, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [childrenCount, isActive, ref]);
};

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (element: T) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = element;
      }
    });
  };
}

const ScrollArea = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    observeResize?: boolean; // Custom prop: legacy prop (unused, kept for compatibility)
    infiniteScrollRef?:
      | React.RefObject<HTMLDivElement | null>
      | ((node: HTMLDivElement | null) => void | (() => void)); // Custom prop: ref for infinite scroll viewport
  }
>(
  (
    { className, children, observeResize, infiniteScrollRef, ...props },
    ref,
  ) => {
    const internalViewportRef = React.useRef<HTMLDivElement>(null);

    const childrenCount = React.Children.count(children);
    if (observeResize)
      useResizeObserver(
        internalViewportRef,
        childrenCount,
        children,
        observeResize,
      );

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={cn('relative flex flex-col overflow-hidden', className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={mergeRefs(internalViewportRef, infiniteScrollRef)}
          className="min-h-0 w-full flex-1 rounded-[inherit]"
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' &&
        'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
